import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Match, Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";

import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { useOrders } from "#orders-context.tsx";
import { OrderToolbar } from "#order-toolbar.tsx";
import { useLanguage } from "#i18n.tsx";
import {
  defaultPreorderFilters,
  preorderStatusSchema,
  readPreorderFilters,
  writePreorderFilters,
  type PreorderFilters,
} from "#order-view-search.ts";
import { useViewSearch } from "#use-view-search.ts";

const statusFilters = preorderStatusSchema.literals;
const defaultFilters = defaultPreorderFilters;

const statusLabel = (status: PreorderFilters["status"]) =>
  status === "RecoveryRequired" ? "Needs review" : status;

const pickupDateLabel = (date: string) =>
  new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));

const preorderSchema = Schema.Struct({
  _tag: Schema.Literal(
    "Confirmed",
    "Completed",
    "Cancelled",
    "RecoveryRequired",
  ),
  id: Schema.String,
  pickupDate: Schema.String,
  customerContact: Schema.Unknown,
  pricing: Schema.Struct({
    total: Schema.Struct({
      amount: Schema.Number,
      currency: Schema.Literal("AUD"),
    }),
    lines: Schema.Array(
      Schema.Struct({
        displayName: Schema.String,
        quantity: Schema.Number,
      }),
    ),
  }),
});

const preorderPageSchema = Schema.Struct({
  items: Schema.Array(preorderSchema),
});

const contactSchema = Schema.Struct({
  _tag: Schema.Literal("ContactPresent"),
  customer: Schema.Struct({
    name: Schema.String,
    email: Schema.String,
    phone: Schema.String,
  }),
});

const contactFor = (contact: unknown) => {
  const result = decodeUnknownOption(contactSchema, contact);

  return result?.customer;
};

const money = (amount: number) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount / 100);

/** Staff preorder list with audited, idempotent manual completion. */
export function PreordersPage() {
  const { api } = useAuth();
  const { language, t } = useLanguage();
  const { orders } = useOrders();
  const queryClient = useQueryClient();
  const [searchParams, applySearch] = useViewSearch();
  const filters = readPreorderFilters(searchParams);
  const setFilters = (next: PreorderFilters) => {
    const nextSearch = writePreorderFilters(searchParams, next);
    const unchanged =
      nextSearch.toString() ===
      writePreorderFilters(searchParams, filters).toString();
    applySearch(nextSearch, { replace: unchanged });
  };
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const { status: filter, pickupFrom, pickupTo } = filters;
  const ordersRevision = orders.reduce(
    (latest, order) => (order.updatedAt > latest ? order.updatedAt : latest),
    "",
  );
  const observedOrdersRevision = useRef(ordersRevision);
  const preorders = useQuery<typeof preorderPageSchema.Type, ApiError>({
    queryKey: ["preorders", filters],
    retry: false,
    queryFn: ({ signal }) => {
    const query = new URLSearchParams({ programId: "bay-grocer-fridays" });

    if (filter !== "All") query.set("state", filter);

    if (pickupFrom) query.set("pickupFrom", pickupFrom);

    if (pickupTo) query.set("pickupTo", pickupTo);

      return runEffectQuery(
        api.requestEffect({
          path: `/v1/admin/preorders?${query}`,
          schema: preorderPageSchema,
          signal,
        }),
        signal,
      );
    },
  });
  useEffect(() => {
    if (ordersRevision === observedOrdersRevision.current) return;

    observedOrdersRevision.current = ordersRevision;
    const timer = setTimeout(
      () => void queryClient.invalidateQueries({ queryKey: ["preorders"] }),
      1500,
    );

    return () => clearTimeout(timer);
  }, [ordersRevision, queryClient]);
  const completion = useMutation<
    typeof preorderSchema.Type,
    ApiError,
    string
  >({
    retry: false,
    mutationFn: (preorderId: string) =>
      runEffectMutation(
        api.requestEffect({
          path: `/v1/admin/preorders/${preorderId}/complete`,
          method: "POST",
          idempotencyKey: crypto.randomUUID(),
          schema: preorderSchema,
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["preorders"] }),
  });
  const completing = completion.isPending ? completion.variables : undefined;

  return (
    <div>
      <OrderToolbar
        title={t("preorders.title")}
        summary={[
          statusLabel(filter),
          ...(pickupFrom ? [`From ${pickupDateLabel(pickupFrom)}`] : []),
          ...(pickupTo ? [`To ${pickupDateLabel(pickupTo)}`] : []),
        ].join(" · ")}
        modified={filter !== "Confirmed" || Boolean(pickupFrom || pickupTo)}
        open={() => setDraftFilters(filters)}
        apply={() => setFilters(draftFilters)}
        reset={() => setDraftFilters(defaultFilters)}
        resetApplied={() => {
          setFilters(defaultFilters);
          setDraftFilters(defaultFilters);
        }}
      >
        <h3>{t("preorders.status")}</h3>
        <div className="filters" role="group" aria-label="Filter preorders">
          {statusFilters.map((value) => (
            <button
              aria-pressed={draftFilters.status === value}
              className={draftFilters.status === value ? "active" : ""}
              type="button"
              key={value}
              onClick={() =>
                setDraftFilters((current) => ({ ...current, status: value }))
              }
            >
              {statusLabel(value)}
            </button>
          ))}
        </div>
        <div className="date-filters">
          <label className="field">
            Pickup from
            <input
              type="date"
              value={draftFilters.pickupFrom}
              max={draftFilters.pickupTo}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  pickupFrom: event.target.value,
                }))
              }
            />
          </label>
          <label className="field">
            Pickup to
            <input
              type="date"
              value={draftFilters.pickupTo}
              min={draftFilters.pickupFrom}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  pickupTo: event.target.value,
                }))
              }
            />
          </label>
        </div>
        <p className="muted">
          Bay Grocer Fridays. Paid orders for upcoming pickups. Mark collected
          after handover. Customer details are removed 24 hours after
          completion.
        </p>
      </OrderToolbar>
      {completion.error &&
        Match.valueTags(completion.error, {
          ApiError: (failure) => (
            <p className="alert" role="alert">
              {failure.message}
            </p>
          ),
        })}
      {Match.value(preorders).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("preorders.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data, isFetching }) =>
          data.items.length === 0 ? (
            <div className="empty" aria-busy={isFetching}>
              <h2>
                No {filter === "All" ? "" : filter.toLowerCase()} preorders
              </h2>
              <p>Try another status or pickup date.</p>
            </div>
          ) : (
            <div className="preorder-list" aria-busy={isFetching}>
              {data.items.map((preorder) => {
            const contact = contactFor(preorder.customerContact);

            return (
              <article className="preorder-card" key={preorder.id}>
                <div className="preorder-main">
                  <div className="card-row">
                    <span
                      className={`pill ${preorder._tag === "Confirmed" ? "paid" : ""}`}
                    >
                      {preorder._tag}
                    </span>
                    <strong>
                      {contact?.name ?? "Customer details removed"}
                    </strong>
                  </div>
                  <p>
                    {preorder.pricing.lines
                      .map((line) => `${line.quantity} × ${line.displayName}`)
                      .join(", ")}
                  </p>
                  {contact && (
                    <p className="contact">
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </p>
                  )}
                </div>
                <dl>
                  <div>
                    <dt>{t("preorders.pickup")}</dt>
                    <dd>
                      {new Intl.DateTimeFormat(language, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      }).format(new Date(`${preorder.pickupDate}T12:00:00`))}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("preorders.total")}</dt>
                    <dd>{money(preorder.pricing.total.amount)}</dd>
                  </div>
                </dl>
                {preorder._tag === "Confirmed" && (
                  <button
                    className="primary compact"
                    disabled={completing === preorder.id}
                    type="button"
                    onClick={() => completion.mutate(preorder.id)}
                  >
                    <Check aria-hidden="true" />
                    {completing === preorder.id
                      ? t("preorders.completing")
                      : t("preorders.markCollected")}
                  </button>
                )}
              </article>
            );
              })}
            </div>
          ),
        ),
        Match.exhaustive,
      )}
    </div>
  );
}
